import { Component, HostListener, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService, CurrentUser } from '../auth.service';
import {
  DevPriorityHoleFiller,
  DevPriorityHoleItem,
  DevPriorityHoleKind,
  DevPriorityHoleNote,
  DevPriorityHolePriority,
  DevPriorityHoleStatus,
} from '../models/dev-priority-hole.model';

type DevPriorityHoleStatusFilter = DevPriorityHoleStatus | 'all';
type DevPriorityHolePriorityFilter = DevPriorityHolePriority | 'all';
type DevPriorityHoleKindFilter = DevPriorityHoleKind | 'all';
type DevPriorityHoleFillerFilter = string;
type DevPriorityHoleAreaFilter = string;
type DevPriorityHoleSortKey = 'priority' | 'updated_at' | 'created_at' | 'fillers' | 'status' | 'kind' | 'area';
type DevPriorityHoleSortDirection = 'asc' | 'desc';
interface DevPriorityHoleOption<T> {
  value: T;
  label: string;
}

interface DevPriorityHoleTextOption {
  value: string;
  label: string;
}


interface DevPriorityHoleFilters {
  status: DevPriorityHoleStatusFilter;
  priority: DevPriorityHolePriorityFilter;
  kind: DevPriorityHoleKindFilter;
  filler: DevPriorityHoleFillerFilter;
  area: DevPriorityHoleAreaFilter;
  search: string;
  showArchived: boolean;
  showCompleted: boolean;
  sortBy: DevPriorityHoleSortKey;
  sortDirection: DevPriorityHoleSortDirection;
}

interface DevPriorityHoleReferenceOption {
  path: string;
  label: string;
  group: string;
}

interface DevPriorityHoleListResponse {
  items?: DevPriorityHoleItem[];
  reference_options?: DevPriorityHoleReferenceOption[];
  message?: string;
}

interface DevPriorityHoleItemResponse {
  item?: DevPriorityHoleItem;
  message?: string;
}

interface DevPriorityHoleReferenceResponse {
  reference_options?: DevPriorityHoleReferenceOption[];
  reference_count?: number;
  generated_at?: string;
  message?: string;
}

interface DevPriorityHoleSummaryTile {
  label: string;
  value: number;
  tone: 'open' | 'active' | 'blocked' | 'review' | 'done' | 'critical';
}

@Component({
  selector: 'app-dev-priority-hole',
  templateUrl: './dev-priority-hole.component.html',
  styleUrls: ['./dev-priority-hole.component.scss']
})
export class DevPriorityHoleComponent implements OnInit {
  private readonly apiUrl = '/api/dev_priority_hole/dev_priority_hole.php';
  private readonly allowedHost = 'dams.ulaap.com';
  private readonly allowedEmails = new Set([
    'allan@ulaap.com',
    'wayne@ulaap.com',
    'jdo@ulaap.com',
    'byron.chisholm@gmail.com',
  ]);
  private readonly priorityRank: Record<DevPriorityHolePriority, number> = {
    low: 1,
    normal: 2,
    high: 3,
    critical: 4,
  };
  private readonly statusRank: Record<DevPriorityHoleStatus, number> = {
    open: 1,
    active: 2,
    blocked: 3,
    review: 4,
    done: 5,
  };
  private readonly kindRank: Record<DevPriorityHoleKind, number> = {
    bug: 1,
    broken_workflow: 2,
    bad_ui: 3,
    missing_feature: 4,
    unknown_requirement: 5,
    cleanup: 6,
    loose_end: 7,
    other: 8,
  };
  private readonly baseTagOptions = [
    'api',
    'auth',
    'backend',
    'db',
    'deploy',
    'forms',
    'frontend',
    'permissions',
    'postgres',
    'regression',
    'routing',
    'ui',
    'workflow',
  ];
  private readonly baseAreaOptions = [
    'admin',
    'api',
    'auth',
    'contacts',
    'dashboard',
    'dev tools',
    'drop trailers',
    'financials',
    'lading',
    'menu',
    'mobile',
    'rates',
    'shipments',
    'users',
    'work orders',
  ];
  private readonly baseFileRefOptions = [
    'src/app/dev-priority-hole/dev-priority-hole.component.ts',
    'src/app/dev-priority-hole/dev-priority-hole.component.html',
    'src/app/dev-priority-hole/dev-priority-hole.component.scss',
    'src/app/models/dev-priority-hole.model.ts',
    'api/dev_priority_hole/dev_priority_hole.php',
    'src/app/app-routing.module.ts',
    'src/app/app.module.ts',
  ];

  readonly statusOptions: DevPriorityHoleOption<DevPriorityHoleStatus>[] = [
    { value: 'open', label: 'Open' },
    { value: 'active', label: 'Filling' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'review', label: 'Review' },
    { value: 'done', label: 'Filled' },
  ];
  readonly priorityOptions: DevPriorityHoleOption<DevPriorityHolePriority>[] = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];
  readonly kindOptions: DevPriorityHoleOption<DevPriorityHoleKind>[] = [
    { value: 'bug', label: 'Bug' },
    { value: 'missing_feature', label: 'Missing Feature' },
    { value: 'cleanup', label: 'Cleanup' },
    { value: 'broken_workflow', label: 'Broken Workflow' },
    { value: 'bad_ui', label: 'Bad UI' },
    { value: 'unknown_requirement', label: 'Unknown Requirement' },
    { value: 'loose_end', label: 'Loose End' },
    { value: 'other', label: 'Other' },
  ];
  readonly sortOptions: DevPriorityHoleOption<DevPriorityHoleSortKey>[] = [
    { value: 'priority', label: 'Priority' },
    { value: 'updated_at', label: 'Updated' },
    { value: 'created_at', label: 'Created' },
    { value: 'fillers', label: 'Fillers' },
    { value: 'status', label: 'Status' },
    { value: 'kind', label: 'Kind' },
    { value: 'area', label: 'Area' },
  ];

  itemForm: FormGroup;
  filtersForm: FormGroup;
  noteForm: FormGroup;
  noteEditForm: FormGroup;

  items: DevPriorityHoleItem[] = [];
  referenceOptions: DevPriorityHoleReferenceOption[] = [];
  refreshingReferences = false;
  referenceManifestGeneratedAt = "";
  loading = false;
  saving = false;
  errorMessage = '';
  editorOpen = false;
  editingUuid: string | null = null;
  expandedUuid: string | null = null;
  activeNoteItemUuid: string | null = null;
  editingNoteUuid: string | null = null;
  pendingBuryUuid: string | null = null;
  busyUuid: string | null = null;
  accessChecked = false;
  accessAllowed = false;
  accessDeniedMessage = '';
  currentUserEmail = '';

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private authService: AuthService
  ) {
    this.itemForm = this.fb.group({
      title: ['', Validators.required],
      kind: ['other', Validators.required],
      area: [null],
      priority: ['normal', Validators.required],
      status: ['open', Validators.required],
      fileRefs: [[]],
      tags: [[]],
      description: [''],
      triggerSteps: [''],
      expectedResult: [''],
      actualResult: [''],
      doneWhen: [''],
    });

    this.filtersForm = this.fb.group({
      status: ['all'],
      priority: ['all'],
      kind: ['all'],
      filler: ['all'],
      area: ['all'],
      search: [''],
      showArchived: [false],
      showCompleted: [false],
      sortBy: ['priority'],
      sortDirection: ['desc'],
    });

    this.noteForm = this.fb.group({
      body: ['', Validators.required],
    });

    this.noteEditForm = this.fb.group({
      body: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.checkAccess();
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.editorOpen) {
      this.cancelEdit();
      return;
    }

    this.pendingBuryUuid = null;
  }

  get summaryTiles(): DevPriorityHoleSummaryTile[] {
    return [
      { label: 'Open', value: this.countStatus('open'), tone: 'open' },
      { label: 'Filling', value: this.countStatus('active'), tone: 'active' },
      { label: 'Blocked', value: this.countStatus('blocked'), tone: 'blocked' },
      { label: 'Review', value: this.countStatus('review'), tone: 'review' },
      { label: 'Filled', value: this.countStatus('done'), tone: 'done' },
      { label: 'Critical', value: this.criticalCount, tone: 'critical' },
    ];
  }

  get statusFilterOptions(): DevPriorityHoleOption<DevPriorityHoleStatusFilter>[] {
    return [{ value: 'all', label: 'Any status' }, ...this.statusOptions];
  }

  get priorityFilterOptions(): DevPriorityHoleOption<DevPriorityHolePriorityFilter>[] {
    return [{ value: 'all', label: 'Any priority' }, ...this.priorityOptions];
  }

  get kindFilterOptions(): DevPriorityHoleOption<DevPriorityHoleKindFilter>[] {
    return [{ value: 'all', label: 'Any kind' }, ...this.kindOptions];
  }

  get fillerFilterOptions(): DevPriorityHoleOption<string>[] {
    return [
      { value: 'all', label: 'Any filler' },
      ...this.fillerOptions.map((filler) => ({ value: filler, label: filler })),
    ];
  }

  get areaFilterOptions(): DevPriorityHoleOption<string>[] {
    return [
      { value: 'all', label: 'Any area' },
      ...this.areaOptions.map((area) => ({ value: area, label: area })),
    ];
  }

  get filteredItems(): DevPriorityHoleItem[] {
    const filters = this.filterSnapshot();
    const query = filters.search.toLowerCase();
    const includeDoneBecauseSelected = filters.status === 'done';

    const filtered = this.items.filter((item) => {
      if (!filters.showArchived && item.archived) {
        return false;
      }

      if (!filters.showCompleted && item.status === 'done' && !includeDoneBecauseSelected) {
        return false;
      }

      if (filters.status !== 'all' && item.status !== filters.status) {
        return false;
      }

      if (filters.priority !== 'all' && item.priority !== filters.priority) {
        return false;
      }

      if (filters.kind !== 'all' && item.kind !== filters.kind) {
        return false;
      }

      if (filters.area !== 'all' && item.area !== filters.area) {
        return false;
      }

      if (filters.filler !== 'all' && !item.fillers.some((filler) => filler.email === filters.filler)) {
        return false;
      }

      if (query !== '' && !this.itemMatchesSearch(item, query)) {
        return false;
      }

      return true;
    });

    return filtered.sort((left, right) => {
      const direction = filters.sortDirection === 'asc' ? 1 : -1;
      return this.compareItems(left, right, filters.sortBy) * direction;
    });
  }

  get fillerOptions(): string[] {
    return this.sortedUnique(this.items.flatMap((item) => item.fillers.map((filler) => filler.email.trim())));
  }

  get areaOptions(): string[] {
    return this.sortedUnique([
      ...this.baseAreaOptions,
      ...this.items.map((item) => item.area),
    ]);
  }

  get areaSelectOptions(): DevPriorityHoleTextOption[] {
    return this.areaOptions.map((area) => ({ value: area, label: area }));
  }

  get tagOptions(): string[] {
    return this.sortedUnique([
      ...this.baseTagOptions,
      ...this.items.flatMap((item) => item.tags),
    ]);
  }

  get tagSelectOptions(): DevPriorityHoleTextOption[] {
    return this.tagOptions.map((tag) => ({ value: tag, label: tag }));
  }

  get fileRefOptions(): DevPriorityHoleReferenceOption[] {
    const options = new Map<string, DevPriorityHoleReferenceOption>();
    const addOption = (option: DevPriorityHoleReferenceOption): void => {
      const normalized = this.normalizeReferenceOption(option);

      if (normalized.path !== "") {
        options.set(normalized.path, normalized);
      }
    };

    this.referenceOptions.forEach((option) => addOption(option));
    this.baseFileRefOptions.forEach((path) => addOption(this.referenceOptionFromPath(path, "Pinned")));
    this.items.flatMap((item) => item.file_refs).forEach((path) => addOption(this.referenceOptionFromPath(path, "From holes")));
    this.parseStringArray(this.itemForm.get("fileRefs")?.value).forEach((path) => addOption(this.referenceOptionFromPath(path, "Selected")));

    return Array.from(options.values()).sort((left, right) => {
      const groupCompare = left.group.localeCompare(right.group);
      return groupCompare === 0 ? left.path.localeCompare(right.path) : groupCompare;
    });
  }

  get visibleCount(): number {
    return this.filteredItems.length;
  }

  get totalCount(): number {
    return this.items.length;
  }

  get activeCount(): number {
    return this.items.filter((item) => !item.archived && item.status !== 'done').length;
  }

  get activeFilterCount(): number {
    const filters = this.filterSnapshot();
    let count = 0;

    if (filters.status !== 'all') count++;
    if (filters.priority !== 'all') count++;
    if (filters.kind !== 'all') count++;
    if (filters.area !== 'all') count++;
    if (filters.filler !== 'all') count++;
    if (filters.search !== '') count++;
    if (filters.showArchived) count++;
    if (filters.showCompleted) count++;

    return count;
  }

  get criticalCount(): number {
    return this.items.filter((item) => !item.archived && item.status !== 'done' && item.priority === 'critical').length;
  }

  get emptyMessage(): string {
    return this.activeFilterCount > 0 ? 'No matching holes.' : 'No holes yet.';
  }

  get editorTitle(): string {
    return this.editingUuid ? 'Edit Hole' : 'Expose Hole';
  }

  get sortDirectionLabel(): string {
    return this.filterSnapshot().sortDirection === 'asc' ? 'Ascending' : 'Descending';
  }

  readonly selectTrackBy = (item: unknown): string => {
    if (item && typeof item === 'object') {
      const option = item as { value?: unknown; path?: unknown; email?: unknown; label?: unknown };
      return this.cleanString(option.path ?? option.value ?? option.email ?? option.label);
    }

    return this.cleanString(item);
  };

  readonly fileReferenceSearch = (term: string, item: DevPriorityHoleReferenceOption): boolean => {
    const query = this.cleanString(term).toLowerCase();

    if (query === '') {
      return true;
    }

    return [item.path, item.label, item.group].join(' ').toLowerCase().includes(query);
  };

  readonly textOptionSearch = (term: string, item: DevPriorityHoleTextOption): boolean => {
    const query = this.cleanString(term).toLowerCase();

    if (query === '') {
      return true;
    }

    return [item.value, item.label].join(' ').toLowerCase().includes(query);
  };

  readonly addPlainTextOption = (term: string): DevPriorityHoleTextOption => {
    const value = this.cleanString(term);
    return { value, label: value };
  };

  readonly addFileReferenceTag = (term: string): DevPriorityHoleReferenceOption => {
    return this.referenceOptionFromPath(term, 'Custom');
  };

  loadItems(): void {
    this.loading = true;
    this.errorMessage = '';

    this.http.get<DevPriorityHoleListResponse>(this.apiUrl).subscribe({
      next: (response) => {
        this.items = (response.items ?? []).map((item) => this.normalizeItem(item));
        this.referenceOptions = (response.reference_options ?? []).map((option) => this.normalizeReferenceOption(option));
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.errorFromResponse(error, 'Could not load holes.');
        this.loading = false;
      },
    });
  }

  refreshReferenceOptions(): void {
    this.refreshingReferences = true;
    this.errorMessage = '';

    this.http.post<DevPriorityHoleReferenceResponse>(this.apiUrl, { action: 'refresh_reference_options' }).subscribe({
      next: (response) => {
        this.referenceOptions = (response.reference_options ?? []).map((option) => this.normalizeReferenceOption(option));
        this.referenceManifestGeneratedAt = response.generated_at ?? '';
        this.refreshingReferences = false;
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.errorFromResponse(error, 'Could not refresh file list.');
        this.refreshingReferences = false;
      },
    });
  }

  startCreate(): void {
    this.editingUuid = null;
    this.editorOpen = true;
    this.itemForm.reset(this.emptyItemFormValue());
  }

  startEdit(item: DevPriorityHoleItem): void {
    this.editingUuid = item.uuid;
    this.editorOpen = true;
    this.expandedUuid = item.uuid;
    this.itemForm.reset({
      title: item.title,
      kind: item.kind,
      area: item.area || null,
      priority: item.priority,
      status: item.status,
      fileRefs: [...item.file_refs],
      tags: [...item.tags],
      description: item.description,
      triggerSteps: item.trigger_steps,
      expectedResult: item.expected_result,
      actualResult: item.actual_result,
      doneWhen: item.done_when,
    });
  }

  cancelEdit(): void {
    this.editorOpen = false;
    this.editingUuid = null;
    this.itemForm.reset(this.emptyItemFormValue());
  }

  saveItem(): void {
    if (this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }

    const values = this.itemForm.getRawValue();
    const editingUuid = this.editingUuid;
    const payload: Record<string, unknown> = {
      action: editingUuid ? 'update_item' : 'create_item',
      uuid: editingUuid ?? this.makeUuid(),
      title: this.cleanString(values['title']),
      description: this.cleanString(values['description']),
      kind: this.cleanKind(values['kind']),
      area: this.cleanString(values['area']),
      file_refs: this.parseStringArray(values['fileRefs']),
      trigger_steps: this.cleanString(values['triggerSteps']),
      expected_result: this.cleanString(values['expectedResult']),
      actual_result: this.cleanString(values['actualResult']),
      done_when: this.cleanString(values['doneWhen']),
      priority: this.cleanPriority(values['priority']),
      status: this.cleanStatus(values['status']),
      tags: this.parseStringArray(values['tags']),
    };

    this.saving = true;
    this.postItemAction(payload, () => {
      this.saving = false;
      this.cancelEdit();
    }, () => {
      this.saving = false;
    });
  }

  markFilled(item: DevPriorityHoleItem): void {
    this.postItemAction({ action: 'set_status', uuid: item.uuid, status: 'done' });
  }

  reopen(item: DevPriorityHoleItem): void {
    this.postItemAction({ action: 'set_status', uuid: item.uuid, status: 'open' });
  }

  startFilling(item: DevPriorityHoleItem): void {
    this.postItemAction({ action: 'start_filling', uuid: item.uuid });
  }

  stopFilling(item: DevPriorityHoleItem): void {
    this.postItemAction({ action: 'stop_filling', uuid: item.uuid });
  }

  isFilledByCurrentUser(item: DevPriorityHoleItem): boolean {
    return this.currentUserEmail !== '' && item.fillers.some((filler) => filler.email.toLowerCase() === this.currentUserEmail);
  }

  askBury(item: DevPriorityHoleItem): void {
    if (item.archived) {
      this.setArchived(item, false);
      return;
    }

    this.pendingBuryUuid = item.uuid;
  }

  confirmBury(item: DevPriorityHoleItem): void {
    this.pendingBuryUuid = null;
    this.setArchived(item, true);
  }

  cancelBury(): void {
    this.pendingBuryUuid = null;
  }

  toggleExpanded(item: DevPriorityHoleItem): void {
    this.expandedUuid = this.expandedUuid === item.uuid ? null : item.uuid;
    this.activeNoteItemUuid = null;
    this.editingNoteUuid = null;
  }

  startAddNote(item: DevPriorityHoleItem): void {
    this.expandedUuid = item.uuid;
    this.activeNoteItemUuid = item.uuid;
    this.editingNoteUuid = null;
    this.noteForm.reset({ body: '' });
  }

  addNote(item: DevPriorityHoleItem): void {
    if (this.noteForm.invalid) {
      this.noteForm.markAllAsTouched();
      return;
    }

    const body = this.cleanString(this.noteForm.getRawValue()['body']);
    this.postItemAction(
      {
        action: 'add_note',
        item_uuid: item.uuid,
        uuid: this.makeUuid(),
        body,
      },
      () => {
        this.activeNoteItemUuid = null;
        this.noteForm.reset({ body: '' });
      }
    );
  }

  startEditNote(item: DevPriorityHoleItem, note: DevPriorityHoleNote): void {
    this.expandedUuid = item.uuid;
    this.activeNoteItemUuid = null;
    this.editingNoteUuid = note.uuid;
    this.noteEditForm.reset({ body: note.body });
  }

  saveNoteEdit(note: DevPriorityHoleNote): void {
    if (this.noteEditForm.invalid) {
      this.noteEditForm.markAllAsTouched();
      return;
    }

    const body = this.cleanString(this.noteEditForm.getRawValue()['body']);
    this.postItemAction(
      {
        action: 'update_note',
        uuid: note.uuid,
        body,
      },
      () => {
        this.editingNoteUuid = null;
        this.noteEditForm.reset({ body: '' });
      }
    );
  }

  cancelNoteEdit(): void {
    this.editingNoteUuid = null;
    this.noteEditForm.reset({ body: '' });
  }

  cancelAddNote(): void {
    this.activeNoteItemUuid = null;
    this.noteForm.reset({ body: '' });
  }

  clearFilters(): void {
    this.filtersForm.reset({
      status: 'all',
      priority: 'all',
      kind: 'all',
      filler: 'all',
      area: 'all',
      search: '',
      showArchived: false,
      showCompleted: false,
      sortBy: 'priority',
      sortDirection: 'desc',
    });
  }

  flipSortDirection(): void {
    const current = this.filterSnapshot().sortDirection;
    this.filtersForm.patchValue({ sortDirection: current === 'asc' ? 'desc' : 'asc' });
  }

  statusLabel(status: DevPriorityHoleStatus): string {
    const option = this.statusOptions.find((candidate) => candidate.value === status);
    return option?.label ?? status;
  }

  priorityLabel(priority: DevPriorityHolePriority): string {
    const option = this.priorityOptions.find((candidate) => candidate.value === priority);
    return option?.label ?? priority;
  }

  kindLabel(kind: DevPriorityHoleKind): string {
    const option = this.kindOptions.find((candidate) => candidate.value === kind);
    return option?.label ?? kind;
  }

  statusChipClass(status: DevPriorityHoleStatus): string {
    return `dev-hole-chip dev-hole-chip--${status}`;
  }

  priorityBadgeClass(priority: DevPriorityHolePriority): string {
    return `dev-hole-priority dev-hole-priority--${priority}`;
  }

  tileClass(tone: DevPriorityHoleSummaryTile['tone']): string {
    return `dev-hole-tile dev-hole-tile--${tone}`;
  }

  itemClass(item: DevPriorityHoleItem): string {
    const classes = ['dev-hole-card'];

    if (item.status === 'done') {
      classes.push('dev-hole-card--done');
    }

    if (item.archived) {
      classes.push('dev-hole-card--archived');
    }

    return classes.join(' ');
  }

  hasDevDetails(item: DevPriorityHoleItem): boolean {
    return [
      item.trigger_steps,
      item.expected_result,
      item.actual_result,
      item.done_when,
    ].some((value) => value.trim() !== '');
  }

  fillerSummary(item: DevPriorityHoleItem): string {
    return item.fillers.length ? item.fillers.map((filler) => filler.email).join(', ') : 'No fillers';
  }

  formatDate(value: string | null): string {
    if (!value) {
      return 'Never';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  trackByItem(_index: number, item: DevPriorityHoleItem): string {
    return item.uuid;
  }

  trackByNote(_index: number, note: DevPriorityHoleNote): string {
    return note.uuid;
  }

  trackByFiller(_index: number, filler: DevPriorityHoleFiller): string {
    return filler.email;
  }

  trackByString(_index: number, value: string): string {
    return value;
  }

  private checkAccess(): void {
    const host = window.location.hostname.toLowerCase();

    if (host !== this.allowedHost) {
      this.denyAccess();
      return;
    }

    this.authService.loadCurrentUser().subscribe({
      next: (currentUser) => this.finishAccessCheck(currentUser),
      error: () => this.denyAccess(),
    });
  }

  private finishAccessCheck(currentUser: CurrentUser | null): void {
    const email = String(currentUser?.email ?? '').trim().toLowerCase();

    if (!this.allowedEmails.has(email)) {
      this.denyAccess();
      return;
    }

    this.currentUserEmail = email;
    this.accessAllowed = true;
    this.accessChecked = true;
    this.loadItems();
  }

  private denyAccess(): void {
    this.accessDeniedMessage = 'Access denied.';
    this.accessAllowed = false;
    this.accessChecked = true;
    this.loading = false;
  }

  private countStatus(status: DevPriorityHoleStatus): number {
    return this.items.filter((item) => !item.archived && item.status === status).length;
  }

  private setArchived(item: DevPriorityHoleItem, archived: boolean): void {
    this.postItemAction({ action: 'set_archived', uuid: item.uuid, archived });
  }

  private filterSnapshot(): DevPriorityHoleFilters {
    const values = this.filtersForm.getRawValue();
    const status = this.cleanStatusFilter(values['status']);
    const priority = this.cleanPriorityFilter(values['priority']);
    const kind = this.cleanKindFilter(values['kind']);
    const sortBy = this.cleanSortKey(values['sortBy']);
    const sortDirection = this.cleanSortDirection(values['sortDirection']);

    return {
      status,
      priority,
      kind,
      filler: this.cleanString(values['filler']) || 'all',
      area: this.cleanString(values['area']) || 'all',
      search: this.cleanString(values['search']),
      showArchived: Boolean(values['showArchived']),
      showCompleted: Boolean(values['showCompleted']),
      sortBy,
      sortDirection,
    };
  }

  private itemMatchesSearch(item: DevPriorityHoleItem, query: string): boolean {
    const haystack = [
      item.title,
      item.description,
      this.kindLabel(item.kind),
      item.area,
      item.file_refs.join(' '),
      item.trigger_steps,
      item.expected_result,
      item.actual_result,
      item.done_when,
      item.tags.join(' '),
      item.fillers.map((filler) => filler.email).join(' '),
    ].join(' ').toLowerCase();

    return haystack.includes(query);
  }

  private compareItems(left: DevPriorityHoleItem, right: DevPriorityHoleItem, sortBy: DevPriorityHoleSortKey): number {
    if (sortBy === 'priority') {
      return this.priorityRank[left.priority] - this.priorityRank[right.priority];
    }

    if (sortBy === 'status') {
      return this.statusRank[left.status] - this.statusRank[right.status];
    }

    if (sortBy === 'kind') {
      return this.kindRank[left.kind] - this.kindRank[right.kind];
    }

    if (sortBy === 'area') {
      return this.compareText(left.area, right.area);
    }

    if (sortBy === 'fillers') {
      const emailCompare = this.compareText(this.firstFillerEmail(left), this.firstFillerEmail(right));
      return emailCompare === 0 ? left.fillers.length - right.fillers.length : emailCompare;
    }

    if (sortBy === 'created_at') {
      return this.compareDate(left.created_at, right.created_at);
    }

    return this.compareDate(left.updated_at, right.updated_at);
  }

  private firstFillerEmail(item: DevPriorityHoleItem): string {
    return item.fillers[0]?.email ?? '';
  }

  private compareText(left: string, right: string): number {
    return left.localeCompare(right, undefined, { sensitivity: 'base' });
  }

  private compareDate(left: string, right: string): number {
    return new Date(left).getTime() - new Date(right).getTime();
  }

  private postItemAction(payload: Record<string, unknown>, onSuccess?: (item: DevPriorityHoleItem) => void, onDone?: () => void): void {
    const uuid = typeof payload['uuid'] === 'string' ? payload['uuid'] as string : null;
    this.busyUuid = uuid;
    this.errorMessage = '';

    this.http.post<DevPriorityHoleItemResponse>(this.apiUrl, payload).subscribe({
      next: (response) => {
        if (response.item) {
          const item = this.normalizeItem(response.item);
          this.upsertItem(item);
          onSuccess?.(item);
        }
        else {
          this.loadItems();
        }

        this.busyUuid = null;
        onDone?.();
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.errorFromResponse(error, 'Could not save hole.');
        this.busyUuid = null;
        onDone?.();
      },
    });
  }

  private upsertItem(item: DevPriorityHoleItem): void {
    const index = this.items.findIndex((candidate) => candidate.uuid === item.uuid);

    if (index >= 0) {
      this.items = [
        ...this.items.slice(0, index),
        item,
        ...this.items.slice(index + 1),
      ];
      return;
    }

    this.items = [item, ...this.items];
  }

  private normalizeItem(item: DevPriorityHoleItem): DevPriorityHoleItem {
    return {
      ...item,
      description: item.description ?? '',
      kind: this.cleanKind(item.kind),
      area: item.area ?? '',
      file_refs: Array.isArray(item.file_refs) ? item.file_refs : [],
      trigger_steps: item.trigger_steps ?? '',
      expected_result: item.expected_result ?? '',
      actual_result: item.actual_result ?? '',
      done_when: item.done_when ?? '',
      fillers: Array.isArray(item.fillers) ? item.fillers : [],
      tags: Array.isArray(item.tags) ? item.tags : [],
      notes: Array.isArray(item.notes) ? item.notes : [],
    };
  }


  private normalizeReferenceOption(option: DevPriorityHoleReferenceOption): DevPriorityHoleReferenceOption {
    const path = this.cleanString(option?.path);
    return this.referenceOptionFromPath(path, this.cleanString(option?.group) || 'Reference', this.cleanString(option?.label));
  }

  private referenceOptionFromPath(pathValue: unknown, group = 'Custom', labelValue = ''): DevPriorityHoleReferenceOption {
    const path = this.cleanString(pathValue);
    const label = this.cleanString(labelValue) || this.referenceLabelFromPath(path);

    return {
      path,
      label,
      group: this.cleanString(group) || 'Custom',
    };
  }

  private referenceLabelFromPath(path: string): string {
    const parts = path.split('/').filter((part) => part !== '');
    return parts.length ? parts[parts.length - 1] : path;
  }
  private errorFromResponse(error: HttpErrorResponse, fallback: string): string {
    const body = error.error as DevPriorityHoleListResponse | DevPriorityHoleItemResponse | DevPriorityHoleReferenceResponse | string | null;

    if (typeof body === 'object' && body !== null && typeof body.message === 'string' && body.message.trim() !== '') {
      return body.message;
    }

    if (typeof body === 'string' && body.trim() !== '') {
      return body;
    }

    return fallback;
  }

  private emptyItemFormValue(): Record<string, unknown> {
    return {
      title: '',
      kind: 'other',
      area: null,
      priority: 'normal',
      status: 'open',
      fileRefs: [],
      tags: [],
      description: '',
      triggerSteps: '',
      expectedResult: '',
      actualResult: '',
      doneWhen: '',
    };
  }

  private cleanString(value: unknown): string {
    return String(value ?? '').trim();
  }

  private parseStringArray(value: unknown): string[] {
    const source = Array.isArray(value) ? value : this.cleanString(value).split(',');

    return source
      .map((item) => this.cleanString(item))
      .filter((item) => item !== '')
      .filter((item, index, items) => items.indexOf(item) === index);
  }

  private sortedUnique(values: string[]): string[] {
    return Array.from(
      new Set(
        values
          .map((value) => value.trim())
          .filter((value) => value !== '')
      )
    ).sort((left, right) => left.localeCompare(right));
  }

  private cleanPriority(value: unknown): DevPriorityHolePriority {
    const priority = this.cleanString(value);
    return this.priorityOptions.some((option) => option.value === priority)
      ? priority as DevPriorityHolePriority
      : 'normal';
  }

  private cleanStatus(value: unknown): DevPriorityHoleStatus {
    const status = this.cleanString(value);
    return this.statusOptions.some((option) => option.value === status)
      ? status as DevPriorityHoleStatus
      : 'open';
  }

  private cleanKind(value: unknown): DevPriorityHoleKind {
    const kind = this.cleanString(value);
    return this.kindOptions.some((option) => option.value === kind)
      ? kind as DevPriorityHoleKind
      : 'other';
  }

  private cleanPriorityFilter(value: unknown): DevPriorityHolePriorityFilter {
    const priority = this.cleanString(value);

    if (priority === 'all') {
      return 'all';
    }

    return this.cleanPriority(priority);
  }

  private cleanStatusFilter(value: unknown): DevPriorityHoleStatusFilter {
    const status = this.cleanString(value);

    if (status === 'all') {
      return 'all';
    }

    return this.cleanStatus(status);
  }

  private cleanKindFilter(value: unknown): DevPriorityHoleKindFilter {
    const kind = this.cleanString(value);

    if (kind === 'all') {
      return 'all';
    }

    return this.cleanKind(kind);
  }

  private cleanSortKey(value: unknown): DevPriorityHoleSortKey {
    const sortBy = this.cleanString(value);
    return this.sortOptions.some((option) => option.value === sortBy)
      ? sortBy as DevPriorityHoleSortKey
      : 'priority';
  }

  private cleanSortDirection(value: unknown): DevPriorityHoleSortDirection {
    return this.cleanString(value) === 'asc' ? 'asc' : 'desc';
  }

  private makeUuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char: string) => {
      const random = Math.floor(Math.random() * 16);
      const value = char === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }
}
